
import React from 'react';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { 
  Star, 
  Music, 
  HeadphonesIcon, 
  Video, 
  Paintbrush, 
  MoveRight, 
  Globe,
  Mic,
  Bookmark,
  Plus
} from 'lucide-react';

interface ServiceProviderProps {
  id: string;
  name: string;
  category: string;
  rating: number;
  ratingCount: number;
  badge?: string;
  price: string;
  description: string;
  image: string;
}

const serviceProviders: ServiceProviderProps[] = [
  {
    id: '1',
    name: 'MasterMix Studios',
    category: 'mixing',
    rating: 4.9,
    ratingCount: 152,
    badge: 'Top Rated',
    price: 'From $120',
    description: 'Professional mixing services with unlimited revisions and premium plugins.',
    image: 'https://images.unsplash.com/photo-1598488035139-bdbb2231ce04?w=600&auto=format&fit=crop&q=60&ixlib=rb-4.0.3'
  },
  {
    id: '2',
    name: 'VocalPolish',
    category: 'vocals',
    rating: 4.8,
    ratingCount: 87,
    price: 'From $75',
    description: 'Vocal tuning, comping and processing to make your vocals stand out.',
    image: 'https://images.unsplash.com/photo-1520523839897-bd0b52f945a0?w=600&auto=format&fit=crop&q=60&ixlib=rb-4.0.3'
  },
  {
    id: '3',
    name: 'BeatCrafter',
    category: 'production',
    rating: 4.7,
    ratingCount: 210,
    badge: 'Popular',
    price: 'From $200',
    description: 'Custom beat production in any genre, including all stems and rights.',
    image: 'https://images.unsplash.com/photo-1619983081593-e2ba5b543168?w=600&auto=format&fit=crop&q=60&ixlib=rb-4.0.3'
  },
  {
    id: '4',
    name: 'VisualBeats',
    category: 'video',
    rating: 4.6,
    ratingCount: 64,
    price: 'From $350',
    description: 'Creative music video production with professional editing and color grading.',
    image: 'https://images.unsplash.com/photo-1536240478700-b869070f9279?w=600&auto=format&fit=crop&q=60&ixlib=rb-4.0.3'
  },
  {
    id: '5',
    name: 'MasterCraft',
    category: 'mastering',
    rating: 4.9,
    ratingCount: 183,
    badge: 'Verified',
    price: 'From $80',
    description: 'Professional mastering service with dedicated hardware and analog processing.',
    image: 'https://images.unsplash.com/photo-1588479839964-9c6fcaf8c51e?w=600&auto=format&fit=crop&q=60&ixlib=rb-4.0.3'
  },
  {
    id: '6',
    name: 'ArtWorks Design',
    category: 'artwork',
    rating: 4.8,
    ratingCount: 124,
    price: 'From $150',
    description: 'Custom album artwork design with unlimited concepts and revisions.',
    image: 'https://images.unsplash.com/photo-1580196923194-ddad5b516c88?w=600&auto=format&fit=crop&q=60&ixlib=rb-4.0.3'
  },
  {
    id: '7',
    name: 'PromoGenius',
    category: 'promotion',
    rating: 4.7,
    ratingCount: 96,
    price: 'From $250',
    description: 'Full promotional packages including playlist pitching and press outreach.',
    image: 'https://images.unsplash.com/photo-1493225457124-a3eb161ffa5f?w=600&auto=format&fit=crop&q=60&ixlib=rb-4.0.3'
  },
  {
    id: '8',
    name: 'LiveSound',
    category: 'performance',
    rating: 4.8,
    ratingCount: 42,
    price: 'From $200',
    description: 'Live sound engineering and equipment rental for local performances.',
    image: 'https://images.unsplash.com/photo-1559519529-0936e4058364?w=600&auto=format&fit=crop&q=60&ixlib=rb-4.0.3'
  },
];

const ServiceCard: React.FC<{ provider: ServiceProviderProps }> = ({ provider }) => {
  return (
    <Card className="overflow-hidden hover:shadow-lg transition-shadow">
      <div className="relative h-48 overflow-hidden">
        <img 
          src={provider.image} 
          alt={provider.name} 
          className="w-full h-full object-cover transition-transform duration-300 hover:scale-105"
        />
        {provider.badge && (
          <Badge className="absolute top-2 right-2 bg-primary text-primary-foreground">
            {provider.badge}
          </Badge>
        )}
      </div>
      <CardHeader className="pb-2">
        <div className="flex justify-between">
          <CardTitle className="text-lg">{provider.name}</CardTitle>
          <div className="flex items-center gap-1">
            <Star className="h-4 w-4 fill-yellow-400 text-yellow-400" />
            <span className="text-sm font-medium">{provider.rating}</span>
            <span className="text-xs text-muted-foreground">({provider.ratingCount})</span>
          </div>
        </div>
        <CardDescription>{provider.description}</CardDescription>
      </CardHeader>
      <CardContent className="pt-0">
        <div className="flex items-center justify-between">
          <span className="font-medium">{provider.price}</span>
          <Button variant="outline" size="sm" className="gap-1">
            <Bookmark className="h-4 w-4" />
            <span>Save</span>
          </Button>
        </div>
      </CardContent>
      <CardFooter className="bg-muted/50 border-t">
        <Button variant="link" className="ml-auto text-sm gap-1 p-0">
          View Profile
          <MoveRight className="h-3 w-3" />
        </Button>
      </CardFooter>
    </Card>
  );
};

const MarketplaceServices: React.FC = () => {
  return (
    <div className="space-y-6">
      <Tabs defaultValue="all" className="w-full">
        <TabsList className="mb-6 grid grid-cols-4 sm:grid-cols-8 w-full">
          <TabsTrigger value="all">All</TabsTrigger>
          <TabsTrigger value="mixing" className="flex items-center gap-1">
            <HeadphonesIcon className="h-4 w-4 hidden sm:inline" />
            <span>Mixing</span>
          </TabsTrigger>
          <TabsTrigger value="mastering">Mastering</TabsTrigger>
          <TabsTrigger value="production">Production</TabsTrigger>
          <TabsTrigger value="vocals">Vocals</TabsTrigger>
          <TabsTrigger value="video" className="flex items-center gap-1">
            <Video className="h-4 w-4 hidden sm:inline" />
            <span>Video</span>
          </TabsTrigger>
          <TabsTrigger value="artwork" className="flex items-center gap-1">
            <Paintbrush className="h-4 w-4 hidden sm:inline" />
            <span>Artwork</span>
          </TabsTrigger>
          <TabsTrigger value="performance" className="flex items-center gap-1">
            <Mic className="h-4 w-4 hidden sm:inline" />
            <span>Live</span>
          </TabsTrigger>
        </TabsList>
        
        <TabsContent value="all" className="mt-0">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {serviceProviders.map(provider => (
              <ServiceCard key={provider.id} provider={provider} />
            ))}
          </div>
        </TabsContent>
        
        {['mixing', 'mastering', 'production', 'vocals', 'video', 'artwork', 'performance'].map(category => (
          <TabsContent key={category} value={category} className="mt-0">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
              {serviceProviders
                .filter(provider => provider.category === category)
                .map(provider => (
                  <ServiceCard key={provider.id} provider={provider} />
                ))}
            </div>
          </TabsContent>
        ))}
      </Tabs>
      
      <div className="flex justify-center pt-4">
        <Button variant="outline" className="gap-2">
          <Globe className="h-4 w-4" />
          <span>Discover More Services</span>
        </Button>
      </div>
      
      <div className="mt-10 border-t pt-10">
        <h2 className="text-2xl font-semibold mb-6">Become a Service Provider</h2>
        <div className="bg-muted/50 rounded-lg p-6">
          <div className="flex flex-col md:flex-row gap-6 items-center">
            <div className="flex-1">
              <h3 className="text-lg font-medium mb-2">Share Your Skills with the Community</h3>
              <p className="text-muted-foreground mb-4">
                Offer your services to musicians worldwide. Whether you're a producer, engineer, designer, 
                or promoter, join our marketplace to connect with clients.
              </p>
              <Button className="gap-2">
                <Plus className="h-4 w-4" />
                <span>Create Service Listing</span>
              </Button>
            </div>
            <div className="w-full md:w-1/3 lg:w-1/4">
              <img 
                src="https://images.unsplash.com/photo-1508700115892-45ecd05ae2ad?w=600&auto=format&fit=crop&q=60&ixlib=rb-4.0.3"
                alt="Service provider" 
                className="rounded-lg object-cover w-full aspect-square"
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MarketplaceServices;
